// store.ts
import { atom } from 'jotai';
import {
  inspectionDB as db,
  inspectionDB,
} from '../../../data/database.structure';
import {
  AssetModel,
  EquipmentModel,
  MineLocationModel,
  ObservableActionTypeModel,
  ObservationModel,
  PartDimensionModel,
  PartDimensionThresholdModel,
} from '../../../data/types';
import { atomWithQuery } from 'jotai-tanstack-query';
import DatabaseObservableItemActionTypeMethods from '../../../data/database.observable-item-action-types.methods';
import DatabaseInspectionsMethods from '../../../data/database.inspections.methods';
import { DataTypesEnum, ObservableLevelsEnum } from '../../../data/enums';
import ColorsHelper from '../../../helpers/colours-helpers';
import ObservationHelpers from '../../../helpers/observationHelpers';
import { toast } from '../../toast';
import {
  EToastType,
  TOAST_DURATIONS,
} from '../../../components/molecules/ToastMessage';
import ActionStatusHelpers from '../../../helpers/actionStatusHelpers';
import WearDisplayHelpers from '../../../helpers/wearDisplayHelpers';

export interface ObservableActionType {
  id: number;
  name: string;
  description: string;
  sortOrder: number;
}

export enum ActionStatusType {
  noAction = 'NoAction',
  measurement = 'Measurement',
  noMeasurements = 'NoMeasurements',
}

export interface ActionStatusBarModel {
  percentage: number;
  actionType: ObservableActionTypeModel | null;
  measurement: number | undefined;
  maxValue: number;
  minValue: number;
}

export class ActionTypeStore {
  /**
   * Returns null when the part dimension's limit is invalid (≤ 0 / missing):
   * no percentage can be calculated — callers must skip percentage logic.
   */
  private calculateWearPercentage({
    measuredValue,
    baselineValue,
    limit,
    wearDirection,
  }: {
    measuredValue: number;
    baselineValue: number;
    limit: number;
    wearDirection?: number;
  }): number | null {
    return ActionStatusHelpers.calculateWearPercentage({
      value: measuredValue,
      baselineValue,
      limit,
      wearDirection,
    });
  }

  /**
   * Resolves the part dimensions applicable to an equipment part + observable
   * item (optionally narrowed to a dimension type). Extracted from
   * getPartActionStatus so display components can reuse the same resolution.
   */
  public async getPartDimensions({
    equipmentPartId,
    observableItemId,
    dimensionTypeName,
  }: {
    equipmentPartId: string;
    observableItemId: number;
    dimensionTypeName?: string;
  }): Promise<PartDimensionModel[]> {
    const equipmentPart =
      await inspectionDB.equipmentParts.get(equipmentPartId);
    if (!equipmentPart) return [];

    let partDimensions = await db.partDimensions
      .where({
        observableItemId: observableItemId,
        partId: equipmentPart.partId,
      })
      .toArray();

    if (partDimensions.length === 0) return [];

    const partDimensionTypes = await db.dimensionTypes
      .where('id')
      .anyOf(partDimensions.map((d) => d.dimensionTypeId))
      .toArray();

    if (partDimensionTypes.length === 0) return [];

    // If dimensionTypeName is provided, filter partDimensions by the unit
    if (dimensionTypeName) {
      const dimensionType = partDimensionTypes.find(
        (type) => type.name === dimensionTypeName,
      );
      if (!dimensionType) {
        return []; // No matching dimension type found
      }
      // Filter partDimensions based on the dimension type
      partDimensions = partDimensions.filter((partDimension) => {
        return dimensionType.id === partDimension.dimensionTypeId;
      });
    }

    return partDimensions;
  }

  /** Convenience: the first part dimension on record, or undefined. */
  public async getPartDimension(params: {
    equipmentPartId: string;
    observableItemId: number;
    dimensionTypeName?: string;
  }): Promise<PartDimensionModel | undefined> {
    const partDimensions = await this.getPartDimensions(params);
    return partDimensions[0];
  }

  public static async getMinimumWearValue(partId: number): Promise<number> {
    const partDimensions = await db.partDimensions
      .where('partId')
      .equals(partId)
      .toArray();

    if (partDimensions.length === 0) {
      return 0;
    }

    const minWearValue = Math.min(
      ...partDimensions.map((dimension) => dimension.baselineValue),
    );

    return minWearValue;
  }

  public static validateActionStatus({
    actionStatus,
    multiplier = 1,
    value,
    unit,
  }: {
    actionStatus: ActionStatusBarModel;
    multiplier?: number;
    /**
     * The entered measurement. When provided, rejection toasts state the
     * acceptable range (#10205) instead of the bare threshold, e.g.
     * "500.0 mm is outside the acceptable range 475.0 – 487.0 mm for this part"
     * rather than "Value equal or below 475". Validation rules are unchanged.
     */
    value?: number;
    /** The entry mode's unit for the range message: 'mm' (default) or '%'. */
    unit?: 'mm' | '%';
  }): boolean {
    // Threshold + message selection is pure and unit-tested in
    // ActionStatusHelpers.evaluateActionStatusValidation; this wrapper only toasts.
    const result = ActionStatusHelpers.evaluateActionStatusValidation({
      percentage: actionStatus.percentage,
      minValue: actionStatus.minValue,
      maxValue: actionStatus.maxValue,
      multiplier,
      value,
      unit,
    });

    if (!result.isValid && result.message) {
      toast.add({
        message: result.message,
        type:
          result.severity === 'warning' ? EToastType.warning : EToastType.alert,
        isOpen: true,
        options: { duration: TOAST_DURATIONS.VALIDATION },
      });
    }

    return result.isValid;
  }

  async getCheckboxHighestImportanceActionByObservations(
    observations: ObservationModel[],
  ): Promise<ObservableActionTypeModel | null> {
    const actions = await new DatabaseObservableItemActionTypeMethods(
      db,
    ).getObservableItemActionTypes();

    const checkboxObservations = observations.filter(
      (element) =>
        element.observableItem.dataTypeId === DataTypesEnum.boolean ||
        element.observableItem.dataTypeId === DataTypesEnum.string,
    );

    let importance = 1;
    let actionType: ObservableActionTypeModel | null = null;

    for (const observation of checkboxObservations) {
      // const observableItem = observableItems.find(
      //     (element) => element.id === observation.observableItemId
      // );
      // if (!observableItem) continue;

      const checkBoxActions = actions.filter(
        (element) =>
          element.observableItemActionType.observableItemId ===
          observation.observableItem.id,
      );

      if (checkBoxActions.length === 0) continue;

      for (const checkBoxAction of checkBoxActions) {
        if (
          observation.value !==
          checkBoxAction.observableItemActionType.triggerValue
        ) {
          continue;
        }

        if (!checkBoxAction.observableActionType) continue;

        if (checkBoxAction.observableActionType.sortOrder > importance) {
          importance = checkBoxAction.observableActionType.sortOrder;
          actionType = checkBoxAction.observableActionType;
        }
      }
    }

    return actionType;
  }

  async getObservationActionStatus<T>({
    dataType,
    equipmentPartId,
    path,
    value,
  }: {
    dataType: keyof typeof DataTypesEnum;
    equipmentPartId: string;
    path: string;
    value: unknown | T;
  }): Promise<{
    observableActionType: ObservableActionTypeModel;
    partDimensionThreshold?: PartDimensionThresholdModel;
  } | null> {
    const observableItem = await db.observableItems
      .where('identifier')
      .equals(path)
      .first();

    if (!observableItem) return null;

    // Handle boolean/string observations
    if (dataType === 'boolean' || dataType === 'string') {
      if (typeof value !== 'boolean' && typeof value !== 'string') {
        return null;
      }

      const actions = await new DatabaseObservableItemActionTypeMethods(
        db,
      ).getObservableItemActionTypes({
        fieldName: 'observableItemId',
        value: observableItem.id,
      });

      const checkBoxActions = actions.filter(
        (element) => element.observableItem?.id === observableItem.id,
      );

      if (checkBoxActions.length === 0) return null;

      const typedValue = value.toString();

      for (const checkBoxAction of checkBoxActions) {
        if (
          typedValue !== checkBoxAction.observableItemActionType.triggerValue
        ) {
          continue;
        }

        return {
          observableActionType: checkBoxAction.observableActionType ?? {
            id: 1,
            name: 'NoMeasurements',
            description: 'No Measurement',
            sortOrder: 1,
            defaultColourCode: '#BEBCBD',
          },
        };
      }

      return null;
    }

    // Handle number observations
    if (dataType !== 'number') return null;

    const equipmentPart = await db.equipmentParts
      .where('externalId')
      .equals(equipmentPartId)
      .first();

    if (!equipmentPart) return null;

    const partDimensions = await db.partDimensions
      .where({
        observableItemId: observableItem.id,
        partId: equipmentPart.part,
      })
      .toArray();

    if (partDimensions.length === 0) return null;

    const observationValue =
      typeof value === 'string' ? parseFloat(value) : Number(value);
    if (isNaN(observationValue)) return null;

    for (const partDimension of partDimensions) {
      const observationValuePercentage = this.calculateWearPercentage({
        measuredValue: observationValue,
        baselineValue: partDimension.baselineValue,
        limit: partDimension.limit,
        wearDirection: partDimension.wearDirection,
      });

      // Invalid limit → no percentage → no threshold can apply
      if (observationValuePercentage === null) continue;

      const thresholds = await db.partDimensionThresholds
        .where('partDimensionId')
        .equals(partDimension.id)
        .reverse()
        .sortBy('minPercentage');

      const threshold = thresholds.find(
        (x) => observationValuePercentage >= x.minPercentage,
      );

      if (threshold) {
        const actionType = await db.observableActionTypes.get(
          threshold.actionTypeId,
        );

        if (actionType) {
          return {
            partDimensionThreshold: threshold,
            observableActionType: actionType,
          };
        }
      }
    }

    return null;
  }

  public async getPartActionStatus({
    equipmentPartId,
    value,
    observableItemId,
    dimensionTypeName,
  }: {
    equipmentPartId: string;
    value: number;
    observableItemId: number;
    dimensionTypeName?: string;
  }): Promise<ActionStatusBarModel> {
    let actionModel: ActionStatusBarModel = {
      percentage: 0,
      actionType: null,
      maxValue: 0,
      minValue: 100,
      measurement: undefined,
    };

    const partDimensions = await this.getPartDimensions({
      equipmentPartId,
      observableItemId,
      dimensionTypeName,
    });

    if (partDimensions.length === 0) {
      return actionModel; // No matching dimensions found
    }

    // let observationValue = 0;

    for (const partDimension of partDimensions) {
      const observationValuePercentage = this.calculateWearPercentage({
        measuredValue: value,
        baselineValue: partDimension.baselineValue,
        limit: partDimension.limit,
        wearDirection: partDimension.wearDirection,
      });

      // Invalid limit → no percentage → no threshold can apply
      if (observationValuePercentage === null) continue;

      const range = WearDisplayHelpers.getReadingRange(partDimension);

      const thresholds = await db.partDimensionThresholds
        .where('partDimensionId')
        .equals(partDimension.id)
        .reverse()
        .sortBy('minPercentage');

      if (thresholds.length === 0) {
        actionModel.percentage = Math.max(
          actionModel.percentage,
          observationValuePercentage,
        );
        continue;
      }

      const threshold = thresholds.find(
        (x) => observationValuePercentage >= x.minPercentage,
      );

      if (!threshold) {
        actionModel.minValue = range.min;
        actionModel.maxValue = range.max;
        actionModel.percentage = -1;
        continue;
      }

      const actionType = await db.observableActionTypes.get(
        threshold.actionTypeId,
      );
      if (!actionType) continue;

      // DISPLAY-ONLY: the action status bar shows wear, not the raw absolute
      // measurement. Stored observations stay RAW.
      const wearValue = WearDisplayHelpers.calculateWearMm(
        value,
        partDimension,
      );

      if (!actionModel.actionType) {
        actionModel = {
          percentage: Math.max(
            actionModel.percentage,
            observationValuePercentage,
          ),
          actionType,
          maxValue: range.max,
          minValue: range.min,
          measurement: wearValue,
        };
        continue;
      }

      if (actionType.sortOrder > actionModel.actionType.sortOrder) {
        actionModel = {
          percentage: Math.max(
            actionModel.percentage,
            observationValuePercentage,
          ),
          actionType,
          maxValue: range.max,
          minValue: range.min,
          measurement: wearValue,
        };
      } else {
        actionModel.percentage = Math.max(
          actionModel.percentage,
          observationValuePercentage,
        );
      }
    }

    return actionModel;
  }

  public async getMeasurementAction(
    observations: ObservationModel[],
    dimensionTypeName?: string,
  ): Promise<ActionStatusBarModel> {
    if (
      observations.length === 0 ||
      observations.every((obs) => obs.value === '')
    ) {
      const actionType = await inspectionDB.observableActionTypes
        .where('name')
        .equals('NoMeasurements')
        .first(); // Default action type
      return {
        percentage: -1,
        actionType: actionType ?? null,
        maxValue: 100,
        minValue: 0,
        measurement: undefined,
      };
    }

    // The status reflects the row the field shows and the push ships, so a stale twin of the
    // same field (#11006) is not scored alongside it.
    observations = ObservationHelpers.keepFirstRowPerField(observations);

    const equipmentPartIds = [
      ...new Set(observations.map((e) => e.entityExternalId)),
    ];
    const equipmentParts = await db.equipmentParts
      .where('externalId')
      .anyOf(equipmentPartIds)
      .toArray();

    let actionModel: ActionStatusBarModel = {
      percentage: -1,
      actionType: null,
      maxValue: 100,
      minValue: 0,
      measurement: undefined,
    };
    const processedObservationIds: string[] = [];

    for (const observation of observations) {
      if (processedObservationIds.includes(observation.externalId)) {
        continue;
      }

      const selectedObservations = observations.filter(
        (element) =>
          element.entityId === observation.entityId &&
          element.dataLevelId === observation.dataLevelId &&
          element.observableItem.id === observation.observableItem.id,
      );

      processedObservationIds.push(
        ...selectedObservations.map((e) => e.externalId),
      );

      // A reading taken before its bucket was re-homed keeps the superseded part id; the
      // device holds the same part under the replacement's id and external id (#11006).
      const equipmentPart =
        equipmentParts.find((element) => element.id === observation.entityId) ??
        equipmentParts.find(
          (element) => element.externalId === observation.entityExternalId,
        );

      if (!equipmentPart) continue;

      let partDimensions = await db.partDimensions
        .where({
          observableItemId: observation.observableItem.id,
          partId: equipmentPart.partId,
        })
        .toArray();

      if (partDimensions.length === 0) continue;

      // If dimensionTypeName is provided, filter partDimensions by the unit
      if (dimensionTypeName) {
        const dimensionType = await db.dimensionTypes
          .where('name')
          .equals(dimensionTypeName)
          .first();
        if (!dimensionType) {
          continue; // No matching dimension type found
        }
        // Filter partDimensions based on the dimension type
        partDimensions = partDimensions.filter((partDimension) => {
          return dimensionType.id === partDimension.dimensionTypeId;
        });
        if (partDimensions.length === 0) {
          continue; // No matching dimensions found for the unit
        }
      }

      let observationValue = 0;

      if (selectedObservations.length > 1) {
        observationValue =
          selectedObservations
            .map((obs) => parseFloat(obs.value) || 0)
            .reduce((a, b) => a + b, 0) / selectedObservations.length;
        observationValue = Math.round(observationValue);
      } else {
        const value = selectedObservations[0].value;
        if (!value || value === 'null' || value === '') continue;
        observationValue = parseFloat(value) || 0;
      }

      for (const partDimension of partDimensions) {
        const observationValuePercentage = this.calculateWearPercentage({
          measuredValue: observationValue,
          baselineValue: partDimension.baselineValue,
          limit: partDimension.limit,
          wearDirection: partDimension.wearDirection,
        });

        // Invalid limit → no percentage → no threshold can apply
        if (observationValuePercentage === null) continue;

        // DISPLAY-ONLY: the action status bar shows wear, not the raw absolute
        // measurement. Stored observations stay RAW.
        const wearValue =
          observation.unit === 'Percentage'
            ? undefined
            : WearDisplayHelpers.calculateWearMm(
                observationValue,
                partDimension,
              );
        const range = WearDisplayHelpers.getReadingRange(partDimension);

        const thresholds = await db.partDimensionThresholds
          .where('partDimensionId')
          .equals(partDimension.id)
          .reverse()
          .sortBy('minPercentage');

        if (thresholds.length === 0) {
          actionModel.percentage = Math.max(
            actionModel.percentage,
            observationValuePercentage,
          );
          continue;
        }

        const threshold = thresholds.find(
          (x) => observationValuePercentage >= x.minPercentage,
        );

        if (!threshold) {
          actionModel.minValue = range.min;
          actionModel.maxValue = range.max;
          continue;
        }

        const actionType = await db.observableActionTypes.get(
          threshold.actionTypeId,
        );
        if (!actionType) continue;

        if (!actionModel.actionType) {
          actionModel = {
            percentage: Math.max(
              actionModel.percentage,
              observationValuePercentage,
            ),
            actionType,
            maxValue: range.max,
            minValue: range.min,
            measurement: wearValue,
          };
          continue;
        }

        if (
          actionType.sortOrder > actionModel.actionType.sortOrder ||
          (actionType.sortOrder === actionModel.actionType.sortOrder &&
            wearValue !== undefined &&
            wearValue >= (actionModel.measurement || 0))
        ) {
          actionModel = {
            percentage: Math.max(
              actionModel.percentage,
              observationValuePercentage,
            ),
            actionType,
            maxValue: range.max,
            minValue: range.min,
            measurement: wearValue,
          };
        } else {
          actionModel.percentage = Math.max(
            actionModel.percentage,
            observationValuePercentage,
          );
        }
      }
    }

    return actionModel;
  }

  async getActionStatus(
    observations: ObservationModel[],
  ): Promise<ActionStatusBarModel> {
    const numberObservations = observations.filter(
      (element) => element.observableItem.dataTypeId === DataTypesEnum.number,
    );

    const actionStatus = await this.getMeasurementAction(numberObservations);
    const measurementField = observations.find((o) =>
      o.observableItem.identifier.includes(ActionStatusType.measurement),
    );
    const checkboxStatus =
      await this.getCheckboxHighestImportanceActionByObservations(
        observations.filter(
          (element) =>
            element.observableItem.dataTypeId === DataTypesEnum.boolean ||
            element.observableItem.dataTypeId === DataTypesEnum.string,
        ),
      );

    const noMeasurementAction = {
      id: 1,
      name: 'NoMeasurements',
      description: 'No Measurement',
      sortOrder: 1,
      defaultColourCode: ColorsHelper.ACTION_STATUS_BAR_COLORS.NoMeasurements,
    };

    const noAction = await db.observableActionTypes
      .where('name')
      .equals('NoAction')
      .first();

    if (!noAction) {
      throw new Error('No action type found for NoAction');
    }

    let actionType: ObservableActionTypeModel = {
      ...noAction,
    } as ObservableActionTypeModel;

    if (observations.length > 0) {
      const observationActionTypes = observations
        .map((e) => e.actionTypeId)
        .sort((a, b) => b - a);

      const largestActionType = observationActionTypes[0];
      const actions = await new DatabaseObservableItemActionTypeMethods(
        db,
      ).getObservableItemActionTypes();

      const action = actions.find(
        (element) =>
          element.observableActionType?.sortOrder === largestActionType,
      );

      if (action) {
        actionType = {
          ...action.observableActionType,
        } as ObservableActionTypeModel;
      }
    }

    if (checkboxStatus && actionStatus.actionType) {
      actionType =
        checkboxStatus.sortOrder >= actionStatus.actionType.sortOrder
          ? checkboxStatus
          : actionStatus.actionType;
    } else if (checkboxStatus) {
      actionType = { ...checkboxStatus } as ObservableActionTypeModel;
    } else if (actionStatus.actionType) {
      actionType = actionStatus.actionType;
    } else if (measurementField && measurementField.value.trim() === '') {
      // a case where measurement input available but did not have any value
      actionType = { ...noMeasurementAction };
    }

    return {
      percentage: Math.round(actionStatus.percentage),
      actionType,
      maxValue: actionStatus.maxValue,
      minValue: actionStatus.minValue,
      measurement: actionStatus.measurement,
    };
  }
}

// Create Jotai atom for the store
export const actionTypeStore = new ActionTypeStore();

interface StatusItem {
  id: string;
  location: string;
  machineName?: string;
  actionStatus: ActionStatusBarModel;
  timestamp: string;
}

interface InspectionHistoryParams {
  externalId: string;
  identifiers: string[];
  pathsLike: string[];
  entityExternalIds: string[];
  dataLevel: ObservableLevelsEnum;
  isOpen: boolean;
}

// Create the params atom
export const inspectionHistoryParamsAtom = atom<InspectionHistoryParams | null>(
  null,
);

// Create the query atom
export const inspectionHistoryAtom = atomWithQuery((get) => ({
  queryKey: ['inspectionHistory', get(inspectionHistoryParamsAtom)] as [
    string,
    InspectionHistoryParams | null,
  ],
  queryFn: async ({
    queryKey,
  }: {
    queryKey: [string, InspectionHistoryParams | null];
  }) => {
    const [, params] = queryKey;
    if (!params || !params.isOpen || !params.externalId) {
      return [];
    }

    try {
      const inspection = await db.inspections
        .where('externalId')
        .equals(params.externalId)
        .first();
      if (!inspection) {
        return [];
      }

      let asset: AssetModel | undefined = undefined;
      let mineLocation: MineLocationModel | undefined = undefined;
      let bucket: EquipmentModel | undefined = undefined;

      asset = await db.assets.where('id').equals(inspection.assetId).first();

      mineLocation = await db.mineLocations
        .where('id')
        .equals(inspection.mineLocationId)
        .first();

      if (inspection.equipmentExternalId) {
        bucket = await db.equipments
          .where('externalId')
          .equals(inspection.equipmentExternalId)
          .first();
      }

      if (!asset && !mineLocation && !bucket) {
        return [];
      }

      const locationName =
        asset !== undefined
          ? asset.siteName
          : mineLocation !== undefined
            ? mineLocation.name
            : bucket !== undefined
              ? bucket.name
              : '';
      const machineName =
        asset !== undefined
          ? asset.name
          : mineLocation !== undefined
            ? mineLocation.name
            : bucket !== undefined
              ? bucket.name
              : '';

      const historyInspections = await new DatabaseInspectionsMethods(
        db,
      ).getInspectionHistory({
        assetId: inspection.assetId > 0 ? inspection.assetId : undefined,
        mineLocationId:
          inspection.mineLocationId > 0 ? inspection.mineLocationId : undefined,
        equipmentExternalId: inspection.equipmentExternalId,
        cutoffDate: inspection.inspectionDate,
      });

      if (params.identifiers.length > 0 || params.pathsLike.length > 0) {
        const obs = historyInspections.map((historicInspection) =>
          // Historic inspections synced without category types (#10006) have no
          // observations to compare against.
          (
            historicInspection.inspectionCategoryTypes[0]?.observations ?? []
          ).filter((observation) => {
            const results = params.identifiers.filter(
              (identifier) =>
                observation.observableItem.identifier.startsWith(identifier) &&
                observation.dataLevelId == params.dataLevel &&
                params.entityExternalIds.includes(observation.entityExternalId),
            );
            const pathsLikeResults = params.pathsLike.filter(
              (pathLike) =>
                observation.observableItem.identifier.startsWith(pathLike) &&
                observation.dataLevelId == params.dataLevel &&
                params.entityExternalIds.includes(observation.entityExternalId),
            );
            return results.length > 0 || pathsLikeResults.length > 0;
          }),
        );

        const processedItems: (StatusItem | undefined)[] = await Promise.all(
          obs.map(async (item, index) => {
            if (item.length === 0) {
              return undefined;
            }
            const actionStatus = await actionTypeStore.getActionStatus(item);
            return {
              id:
                item.length > 0
                  ? item?.[0].id?.toString() +
                    historyInspections[index].inspectionDate.toString()
                  : historyInspections[index].inspectionDate.toString(),
              location: locationName,
              machineName: machineName,
              actionStatus: actionStatus,
              timestamp: historyInspections[index].inspectionDate.toString(),
            };
          }),
        );

        return processedItems.filter(
          (item): item is StatusItem => item !== undefined,
        );
      }

      return [];
    } catch (error) {
      console.error('Error fetching inspection history:', error);
      return [];
    }
  },
  enabled: get(inspectionHistoryParamsAtom)?.isOpen ?? false,
  networkMode: 'always',
}));
